import { LuIcon, Menu, type MenuRootProps, type MenuVariantProps } from "@overmap-ai/blocks"
import type { AssetType, ProcedureInitiative, ProcedureType, Stored, User } from "@overmap-ai/common"
import { memo, type ReactElement, useCallback } from "react"

import { AssetTypeFilterDef } from "../../filtering/defs/AssetTypeFilterDef"
import { CreatedByFilterDef } from "../../filtering/defs/CreatedByFilterDef"
import { ProcedureTypeFilterDef } from "../../filtering/defs/ProcedureTypeFilterDef"
import { SubmittedAtFilterDef } from "../../filtering/defs/SubmittedAtFilterDef"
import { FilterManager } from "../../filtering/manager"
import { modelIconMapping } from "../../mappings/icons"

export interface ProcedureInitiativeFilterMenuProps extends MenuRootProps, MenuVariantProps {
	children: ReactElement
	filterManager: FilterManager<Stored<ProcedureInitiative>>
	procedureTypes: Stored<ProcedureType>[]
	assetTypes: Stored<AssetType>[]
	users: User[]
}

export const ProcedureInitiativeFilterMenu = memo<ProcedureInitiativeFilterMenuProps>((props) => {
	const { children, variant, size, accentColor, filterManager, procedureTypes, assetTypes, users, ...rest } = props

	const handleSelectCreatedBy = useCallback(() => {
		filterManager.addFilter(new CreatedByFilterDef(users).createFilter())
	}, [filterManager, users])

	const handleSelectSubmittedAt = useCallback(() => {
		filterManager.addFilter(new SubmittedAtFilterDef().createFilter())
	}, [filterManager])

	const handleSelectProcedureType = useCallback(() => {
		filterManager.addFilter(new ProcedureTypeFilterDef(procedureTypes).createFilter())
	}, [filterManager, procedureTypes])

	const handleSelectAssetType = useCallback(() => {
		filterManager.addFilter(new AssetTypeFilterDef(assetTypes).createFilter())
	}, [filterManager, assetTypes])

	return (
		<Menu.Root {...rest}>
			<Menu.ClickTrigger>{children}</Menu.ClickTrigger>
			<Menu.Content variant={variant} size={size} accentColor={accentColor}>
				<Menu.Scroll>
					<Menu.Item onSelect={handleSelectCreatedBy}>
						<LuIcon icon={modelIconMapping.creator} />
						Created by
					</Menu.Item>
					<Menu.Item onSelect={handleSelectSubmittedAt}>
						<LuIcon icon={modelIconMapping.dueDate} />
						Submitted at
					</Menu.Item>
					<Menu.Item onSelect={handleSelectProcedureType}>
						<LuIcon icon={modelIconMapping.procedureType} />
						Procedure type
					</Menu.Item>
					<Menu.Item onSelect={handleSelectAssetType}>
						<LuIcon icon={modelIconMapping.assetType} />
						Asset type
					</Menu.Item>
				</Menu.Scroll>
			</Menu.Content>
		</Menu.Root>
	)
})
ProcedureInitiativeFilterMenu.displayName = "ProcedureInitiativeFilterMenu"
